import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Snackbar
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Call as CallIcon,
  Engineering as EngineeringIcon,
  NotificationImportant as AlarmIcon
} from '@mui/icons-material';
import { updateEventPriority } from '../../utils/EventService'; 

// Area to API endpoint mapping
const AREA_API_ENDPOINTS = {
  // Normalized area names (lowercase) to API endpoints
  'barka sayal': 'https://202.140.131.110:2222/api/nearest-devices',
  'argada': 'https://202.140.131.118:2222/api/nearest-devices',
  'north karanpura': 'https://202.140.131.98:2222/api/nearest-devices',
  'bokaro': 'https://202.140.131.67:2222/api/nearest-devices',
  'kargali': 'https://202.140.131.67:2222/api/nearest-devices',
  'bokaro & kargali': 'https://202.140.131.67:2222/api/nearest-devices',
  'kathara': 'https://202.140.131.90:2222/api/nearest-devices',
  'giridih': 'https://202.140.131.82:2222/api/nearest-devices',
  'amrapali': 'https://202.140.131.74:2222/api/nearest-devices',
  'chandragupta': 'https://202.140.131.74:2222/api/nearest-devices',
  'amrapali & chandragupta': 'https://202.140.131.74:2222/api/nearest-devices',
  'rajhara': 'https://202.140.131.102:2222/api/nearest-devices',
  'kuju': 'https://202.140.131.122:2222/api/nearest-devices',
  'hazaribagh': 'https://202.140.131.114:2222/api/nearest-devices',
  'rajrappa': 'https://202.140.131.126:2222/api/nearest-devices',
  'dhori': 'https://202.140.131.86:2222/api/nearest-devices',
  'piparwar': 'https://202.140.131.94:2222/api/nearest-devices'
};

// Default fallback endpoint (Kathara)
const DEFAULT_API_ENDPOINT = 'https://202.140.131.90:2222/api/nearest-devices';

const NearestTechniciansDialog = ({
  open,
  onClose,
  event = null,
  latitude: propLatitude,
  longitude: propLongitude,
  cameraIp: propCameraIp,
  hasValidCoords,
  formattedCoordinates
}) => {
  const eventId = event?.id || event?.eventId;
  const httpsUrl = event?.httpsUrl;
  const currentUser = event?.currentUser || 'System';
  const cameraIp = event?.cameraIp || propCameraIp;
  const latitude = event?.cameraInfo?.latitude || propLatitude;
  const longitude = event?.cameraInfo?.longitude || propLongitude;

  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [alarmingTechnicians, setAlarmingTechnicians] = useState(new Set());
  const [selectedEndpoint, setSelectedEndpoint] = useState(DEFAULT_API_ENDPOINT);
  const [detectedArea, setDetectedArea] = useState('Unknown');
  const [hasInitialized, setHasInitialized] = useState(false); // Add this state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Function to detect area from event data
  const detectAreaFromEvent = useCallback((eventData) => {
    console.group('🌍 Area Detection Process');
    
    // Possible sources for area information
    const areaSources = [
      eventData?.area,
      eventData?.location,
      eventData?.cameraInfo?.area,
      eventData?.cameraInfo?.location,
      eventData?.originalMessage?.area,
      eventData?.originalMessage?.location,
      eventData?.site,
      eventData?.region,
      eventData?.zone
    ];
    
    console.log('📍 Checking area sources:', areaSources);
    
    // Check each source
    for (const source of areaSources) {
      if (source && typeof source === 'string') {
        const normalizedSource = source.toLowerCase().trim();
        console.log(`🔍 Checking source: "${source}" -> "${normalizedSource}"`);
        
        // Direct match
        if (AREA_API_ENDPOINTS[normalizedSource]) {
          console.log(`✅ Direct match found: ${normalizedSource}`);
          console.groupEnd();
          return {
            area: source,
            endpoint: AREA_API_ENDPOINTS[normalizedSource],
            matchType: 'direct'
          };
        }
        
        // Partial match - check if area name contains any of our known areas
        for (const [knownArea, endpoint] of Object.entries(AREA_API_ENDPOINTS)) {
          if (normalizedSource.includes(knownArea) || knownArea.includes(normalizedSource)) {
            console.log(`✅ Partial match found: "${normalizedSource}" contains "${knownArea}"`);
            console.groupEnd();
            return {
              area: source,
              endpoint: endpoint,
              matchType: 'partial'
            };
          }
        }
      }
    }
    
    console.warn('⚠️ No area match found, using default endpoint');
    console.groupEnd();
    return {
      area: 'Unknown',
      endpoint: DEFAULT_API_ENDPOINT,
      matchType: 'default'
    };
  }, []);

  // Function to get appropriate API endpoint based on area
  const getApiEndpoint = useCallback((eventData) => {
    const detection = detectAreaFromEvent(eventData);
    setDetectedArea(detection.area);
    setSelectedEndpoint(detection.endpoint);
    
    console.log('🎯 Area Detection Result:', {
      detectedArea: detection.area,
      selectedEndpoint: detection.endpoint,
      matchType: detection.matchType
    });
    
    return detection.endpoint;
  }, [detectAreaFromEvent]);

  const isValidCoordinate = (value) => {
    if (value === null || value === undefined) return false;
    
    if (typeof value === 'number') {
      return !isNaN(value) && isFinite(value);
    }
    
    if (typeof value === 'string') {
      const trimmed = value.trim().toLowerCase();
      if (trimmed === '' || trimmed === 'n/a' || trimmed === 'null' || trimmed === 'undefined' || trimmed === 'nan') {
        return false;
      }
      
      const parsed = parseFloat(value);
      return !isNaN(parsed) && isFinite(parsed);
    }
    
    return false;
  };

  const parseCoordinate = (value) => {
    if (!isValidCoordinate(value)) return null;
    
    if (typeof value === 'number') {
      return value;
    }
    
    if (typeof value === 'string') {
      const parsed = parseFloat(value.trim());
      return isNaN(parsed) ? null : parsed;
    }
    
    return null;
  };

  const isValidLatitude = (lat) => {
    const parsed = parseCoordinate(lat);
    return parsed !== null && parsed >= -90 && parsed <= 90;
  };

  const isValidLongitude = (lng) => {
    const parsed = parseCoordinate(lng);
    return parsed !== null && parsed >= -180 && parsed <= 180;
  };

  const fetchNearestTechnicians = useCallback(async (lat, lng, apiEndpoint) => {
    setLoading(true);
    setError(null);
    
    try {
      const requestPayload = {
        latitude: Number(lat),
        longitude: Number(lng)
      };

      // console.log('🚀 Fetching technicians from:', {
      //   endpoint: apiEndpoint,
      //   area: detectedArea,
      //   coordinates: requestPayload
      // });

      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`https Error: ${response.status} ${response.statusText}. ${errorText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        const devices = data.data?.nearestDevices || [];
        setTechnicians(devices);
        
        if (devices.length === 0) {
          setError(`No field technicians found in the ${detectedArea} area.`);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch technicians');
      }
    } catch (err) {
      console.error('❌ Error fetching technicians:', err);
      setError(`Failed to fetch nearest technicians from ${detectedArea}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [detectedArea]);

  // Fixed useEffect with proper dependency management
  useEffect(() => {
    if (open && !hasInitialized) {
      console.log('🔄 Dialog opened, initializing...');
      
      // Reset state
      setTechnicians([]);
      setError(null);
      setAlarmingTechnicians(new Set());
      setHasInitialized(true);
      
      // Detect area and get appropriate endpoint
      const apiEndpoint = getApiEndpoint(event);
      
      const parsedLat = parseCoordinate(latitude);
      const parsedLng = parseCoordinate(longitude);
      const latValid = isValidLatitude(latitude);
      const lngValid = isValidLongitude(longitude);
      
      if (latValid && lngValid) {
        fetchNearestTechnicians(parsedLat, parsedLng, apiEndpoint);
      } else {
        let errorMsg = `Unable to find technicians: Camera location coordinates are invalid.\n\n`;
        errorMsg += `Camera IP: ${cameraIp}\n`;
        errorMsg += `Detected Area: ${detectedArea}\n`;
        errorMsg += `API Endpoint: ${apiEndpoint}\n`;
        errorMsg += `Latitude: ${latitude} (${latValid ? 'Valid' : 'Invalid'})\n`;
        errorMsg += `Longitude: ${longitude} (${lngValid ? 'Valid' : 'Invalid'})\n\n`;
        
        if (!latValid && !lngValid) {
          errorMsg += `Both coordinates are invalid. `;
        } else if (!latValid) {
          errorMsg += `Latitude is invalid (must be between -90 and 90). `;
        } else if (!lngValid) {
          errorMsg += `Longitude is invalid (must be between -180 and 180). `;
        }
        
        errorMsg += `Camera might not have GPS enabled or the coordinates are not set correctly.`;
        setError(errorMsg);
      }
    } else if (!open) {
      // Reset state when dialog closes
      setTechnicians([]);
      setError(null);
      setLoading(false);
      setAlarmingTechnicians(new Set());
      setHasInitialized(false);
    }
  }, [open, hasInitialized, latitude, longitude, cameraIp, event, getApiEndpoint, fetchNearestTechnicians, detectedArea]);

  const handleCall = (mobileNumber) => {
    if (mobileNumber) {
      window.open(`tel:${mobileNumber}`);
    }
  };

  const handleAlarmTechnician = async (technician) => {
    if (!eventId) {
      setSnackbar({
        open: true,
        message: 'ERROR: Missing event ID - cannot send alarm',
        severity: 'error'
      });
      return;
    }

    if (!httpsUrl) {
      setSnackbar({
        open: true,
        message: 'ERROR: Missing API URL - cannot send alarm',
        severity: 'error'
      });
      return;
    }

    const technicianKey = technician.deviceId || technician.mobileNumber;
    if (!technicianKey) {
      setSnackbar({
        open: true,
        message: 'ERROR: Technician device ID not available',
        severity: 'error'
      });
      return;
    }

    setAlarmingTechnicians(prev => new Set([...prev, technicianKey]));

    try {
      const timestamp = new Date().toISOString();
      const technicianName = technician.mobileNumber || 'Unknown';
      const mobile = technician.mobileNumber || 'N/A';
      
      const alarmRemark = {
        user: currentUser,
        comment: `ALARM_SENT_TO_TECHNICIAN:${technician.deviceId}|${mobile}|${technicianName}|${timestamp}|${cameraIp}|${detectedArea}`
      };

      const result = await updateEventPriority(httpsUrl, eventId, null, alarmRemark);

      setSnackbar({
        open: true,
        message: `✅ Alarm sent successfully to ${mobile} (${detectedArea})`,
        severity: 'success'
      });

    } catch (error) {
      setSnackbar({
        open: true,
        message: `❌ Failed to send alarm: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setAlarmingTechnicians(prev => {
        const newSet = new Set(prev);
        newSet.delete(technicianKey);
        return newSet;
      });
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatCoordinate = (coord) => {
    const parsed = parseCoordinate(coord);
    if (parsed === null) return 'N/A';
    return parsed.toFixed(6);
  };

  const hasValidCoordinates = () => {
    return isValidLatitude(latitude) && isValidLongitude(longitude);
  };

  const canSendAlarms = () => {
    const hasEventId = !!(eventId && eventId.toString().trim() !== '');
    const hashttpsUrl = !!(httpsUrl && httpsUrl.toString().trim() !== '');
    const hasCurrentUser = !!(currentUser && currentUser.toString().trim() !== '');
    
    return hasEventId && hashttpsUrl && hasCurrentUser;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '300px',
            backgroundColor: '#1a1a1a',
            color: 'white',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1.5}>
              <EngineeringIcon sx={{ color: '#4caf50', fontSize: 28 }} />
              <Typography variant="h6" color="white" fontWeight="600">
                Nearest Field Technicians
              </Typography>
            </Box>
            <IconButton 
              onClick={handleClose} 
              size="small" 
              sx={{ 
                color: 'rgba(255,255,255,0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'white'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {cameraIp && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="subtitle2" color="rgba(255,255,255,0.8)" fontWeight="500">
                Camera: {cameraIp}
              </Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.6)">
                Area: {detectedArea}
                <Box component="span" sx={{ 
                  ml: 1,
                  px: 1,
                  py: 0.25,
                  borderRadius: '4px',
                  backgroundColor: detectedArea !== 'Unknown' ? 'rgba(76,175,80,0.2)' : 'rgba(255,152,0,0.2)',
                  color: detectedArea !== 'Unknown' ? '#4caf50' : '#ff9800',
                  fontSize: '10px',
                  fontWeight: 'bold'
                }}>
                  {detectedArea !== 'Unknown' ? 'DETECTED' : 'DEFAULT'}
                </Box>
              </Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.6)">
                Location: {formatCoordinate(latitude)}, {formatCoordinate(longitude)}
                <Box component="span" sx={{ 
                  ml: 1,
                  px: 1,
                  py: 0.25,
                  borderRadius: '4px',
                  backgroundColor: hasValidCoordinates() ? 'rgba(76,175,80,0.2)' : 'rgba(244,67,54,0.2)',
                  color: hasValidCoordinates() ? '#4caf50' : '#f44336',
                  fontSize: '10px',
                  fontWeight: 'bold'
                }}>
                  {hasValidCoordinates() ? 'VALID' : 'INVALID'}
                </Box>
              </Typography>
              {/* <Typography variant="caption" color="rgba(255,255,255,0.5)" sx={{ display: 'block', mt: 0.5 }}>
                Endpoint: {selectedEndpoint}
              </Typography> */}
            </Box>
          )}
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: '#1a1a1a', px: 3 }}>
          {loading ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={4}>
              <CircularProgress sx={{ color: '#4caf50', mb: 2 }} />
              <Typography variant="body2" color="rgba(255,255,255,0.7)">
                Finding nearest field technicians in {detectedArea}...
              </Typography>
            </Box>
          ) : error ? (
            <Alert 
              severity="warning" 
              sx={{ 
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                color: '#ff9800',
                border: '1px solid rgba(255, 152, 0, 0.3)',
                borderRadius: '8px',
                '& .MuiAlert-message': { 
                  whiteSpace: 'pre-wrap' 
                } 
              }}
            >
              {error}
            </Alert>
          ) : technicians.length === 0 ? (
            <Alert 
              severity="info"
              sx={{ 
                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                color: '#2196f3',
                border: '1px solid rgba(33, 150, 243, 0.3)',
                borderRadius: '8px'
              }}
            >
              No field technicians found in {detectedArea} area.
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="rgba(255,255,255,0.8)" sx={{ mb: 2, fontWeight: 500 }}>
                Found {technicians.length} technician{technicians.length !== 1 ? 's' : ''} in {detectedArea}
              </Typography>
              
              {technicians.map((tech, index) => {
                const technicianKey = tech.deviceId || tech.mobileNumber;
                const isAlarming = alarmingTechnicians.has(technicianKey);
                
                return (
                  <Card 
                    key={tech.deviceId || index} 
                    sx={{ 
                      mb: 2, 
                      backgroundColor: '#242424',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '12px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#2a2a2a',
                        border: '1px solid rgba(255,255,255,0.15)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <PersonIcon sx={{ color: '#4caf50', fontSize: 24 }} />
                          <Typography variant="h6" color="white" fontWeight="600">
                            {tech.mobileNumber || `Technician ${index + 1}`}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip 
                            label={tech.status || 'unknown'}
                            color={tech.status === 'online' ? 'success' : 'error'}
                            size="small"
                            sx={{
                              color: 'white',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                              fontSize: '10px'
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleAlarmTechnician(tech)}
                            disabled={isAlarming || !canSendAlarms()}
                            sx={{
                              color: isAlarming ? '#ff9800' : canSendAlarms() ? '#f44336' : 'rgba(255,255,255,0.3)',
                              backgroundColor: canSendAlarms() ? 'rgba(244, 67, 54, 0.1)' : 'rgba(128,128,128,0.05)',
                              border: canSendAlarms() ? '1px solid rgba(244, 67, 54, 0.3)' : '1px solid rgba(255,255,255,0.1)',
                              borderRadius: '8px',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: canSendAlarms() ? 'rgba(244, 67, 54, 0.2)' : 'rgba(128,128,128,0.1)',
                                border: canSendAlarms() ? '1px solid rgba(244, 67, 54, 0.5)' : '1px solid rgba(255,255,255,0.15)',
                                transform: 'scale(1.05)'
                              },
                              '&:disabled': {
                                color: 'rgba(255,255,255,0.3)',
                                backgroundColor: 'rgba(128,128,128,0.05)'
                              }
                            }}
                            title={
                              !canSendAlarms() 
                                ? "Cannot send alarm: Missing configuration"
                                : isAlarming 
                                  ? "Sending alarm..." 
                                  : `Send alarm to ${tech.mobileNumber} (${detectedArea})`
                            }
                          >
                            {isAlarming ? (
                              <CircularProgress size={18} sx={{ color: '#ff9800' }} />
                            ) : (
                              <AlarmIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                        <LocationIcon fontSize="small" sx={{ color: '#2196f3' }} />
                        <Typography variant="body2" color="rgba(255,255,255,0.9)" fontWeight="500">
                          {tech.distanceFormatted || `${tech.distance || 'Unknown'} km away`}
                        </Typography>
                      </Box>

                      {tech.mobileNumber && (
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <PhoneIcon fontSize="small" sx={{ color: '#4caf50' }} />
                          <Typography variant="body2" color="rgba(255,255,255,0.9)" sx={{ flex: 1, fontWeight: 500 }}>
                            {tech.mobileNumber}
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleCall(tech.mobileNumber)}
                            startIcon={<CallIcon />}
                            sx={{
                              backgroundColor: '#4caf50',
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontWeight: 600,
                              px: 2,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: '#45a049',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 8px rgba(76, 175, 80, 0.3)'
                              }
                            }}
                          >
                            Call
                          </Button>
                        </Box>
                      )}

                      <Divider sx={{ my: 1.5, backgroundColor: 'rgba(255,255,255,0.08)' }} />
                      
                      <Typography variant="caption" color="rgba(255,255,255,0.5)" sx={{ fontSize: '11px' }}>
                        Last updated: {tech.lastUpdateFormatted || 'Unknown'}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          backgroundColor: '#1a1a1a', 
          borderTop: '1px solid rgba(255,255,255,0.08)',
          p: 2.5 
        }}>
          <Button 
            onClick={handleClose} 
            variant="outlined"
            sx={{ 
              color: 'white',
              borderColor: 'rgba(255,255,255,0.3)',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderColor: 'rgba(255,255,255,0.5)'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: '8px'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NearestTechniciansDialog;